import React, {useEffect} from 'react';

import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import type {ProactFormApi} from 'src/app/forms/engine/useProactForm';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import type {IFormDataV2} from 'src/types/FormV2';

export interface DueDatePreviewFormProps {
  form: ProactFormApi;
  isLoading: boolean;
  children?: React.ReactNode;
  /** Full value set on every user-driven change, for consumers mirroring the form into external state. */
  onValuesChange?: (values: IFormDataV2) => void;
}

/** Shared body for task/action forms: syncs a dueInDays field into a read-only previewDueAt preview. */
export const DueDatePreviewForm: React.FC<DueDatePreviewFormProps> = ({
  form,
  isLoading,
  children,
  onValuesChange,
}) => {
  const {methods} = form;

  // Watch-callback (not useWatch) so resets don't overwrite the stored due date loaded from the API.
  useEffect(() => {
    const subscription = methods.watch((values, {name}) => {
      if (name !== 'dueInDays') {
        return;
      }
      const value = values.dueInDays;
      if (value && !isNaN(Number(value))) {
        const date = new Date();
        date.setDate(date.getDate() + Number(value));
        methods.setValue('previewDueAt', date.toString().toShortDate());
      } else {
        methods.setValue('previewDueAt', '');
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  useEffect(() => {
    if (!onValuesChange) {
      return;
    }
    const subscription = methods.watch((values, {name}) => {
      if (name) {
        onValuesChange({...values});
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, onValuesChange]);

  return isLoading === false ? (
    <ProactForm form={form}>
      {children ?? <ProactFormGrid />}
    </ProactForm>
  ) : (
    <TableBodyRender isLoading={isLoading} />
  );
};
